/**
 * Client-side header extraction for the ERP register import wizard (SIL-270,
 * W4-T10 of resources/plans/erp-register-import-spec.md decision 6).
 *
 * The merged Wave 3 API only exposes `POST .../parse`, which REQUIRES a
 * finished `columnMapping` up front (apps/api/src/routes/client/erp-register.ts) —
 * there is no server-side "peek at this file's headers" endpoint, by design
 * (architecture review §3.3: untrusted file bytes are parsed OUTSIDE the API
 * Lambda, in the telemetry-ingest worker, never round-tripped back to the
 * browser). So the column-mapper screen must read the header row itself,
 * client-side, from the File the browser already holds locally — no upload,
 * no network call, nothing crosses a trust boundary.
 *
 * CSV: a small zero-dependency line/field split (mirrors the shape of the
 * server's `parseCsvText`, but only ever reads the first line here).
 * XLSX: `exceljs` (already an audited dependency of apps/telemetry-ingest for
 * the exact same file format) dynamically imported so it never inflates the
 * initial portal bundle — only loaded if the admin picks an .xlsx file.
 */

const HEADER_PEEK_MAX_BYTES = 64 * 1024; // first line only ever needs a small slice

function splitCsvLine(line: string): string[] {
  const result: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        cur += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      result.push(cur.trim());
      cur = '';
    } else {
      cur += ch;
    }
  }
  result.push(cur.trim());
  return result;
}

async function readCsvHeaders(file: File): Promise<string[]> {
  const slice = file.slice(0, HEADER_PEEK_MAX_BYTES);
  const text = await slice.text();
  const firstLine = text.split(/\r\n|\r|\n/).find((l) => l.trim().length > 0) ?? '';
  return splitCsvLine(firstLine).filter((h) => h.length > 0);
}

async function readXlsxHeaders(file: File): Promise<string[]> {
  const ExcelJS = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(await file.arrayBuffer());
  const sheet = workbook.worksheets[0];
  if (!sheet) return [];
  const headerRow = sheet.getRow(1);
  const headers: string[] = [];
  headerRow.eachCell({ includeEmpty: false }, (cell) => {
    const value = cell.value;
    const str = value == null ? '' : typeof value === 'object' && 'text' in (value as object)
      ? String((value as { text: unknown }).text)
      : String(value);
    if (str.trim().length > 0) headers.push(str.trim());
  });
  return headers;
}

export async function readRegisterFileHeaders(file: File, ext: 'csv' | 'xlsx'): Promise<string[]> {
  return ext === 'csv' ? readCsvHeaders(file) : readXlsxHeaders(file);
}
